import React, { useState, useEffect, useRef, useCallback } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';
import './Piano.css';
import '../../ui/ArcadeShared.css';

// Импортируем новые компоненты экранов
import { PianoStartScreen, PianoRoundResultScreen, PianoFinalScreen } from './PianoScreens';

// Конфигурация клавиш (0 = C)
const KEYS = [
  { note: 0, type: 'white', label: 'C' },
  { note: 1, type: 'black', label: '' },
  { note: 2, type: 'white', label: 'D' },
  { note: 3, type: 'black', label: '' },
  { note: 4, type: 'white', label: 'E' },
  { note: 5, type: 'white', label: 'F' },
  { note: 6, type: 'black', label: '' },
  { note: 7, type: 'white', label: 'G' },
  { note: 8, type: 'black', label: '' },
  { note: 9, type: 'white', label: 'A' },
  { note: 10, type: 'black', label: '' },
  { note: 11, type: 'white', label: 'B' },
];

const Piano = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();

  const [state, setState] = useState(null);
  const [pressedKey, setPressedKey] = useState(null);
  const [finalData, setFinalData] = useState(null);
  const isGameOverRef = useRef(false);
  const [playbackIndex, setPlaybackIndex] = useState(-1);
  const [isGameStarted, setIsGameStarted] = useState(false);

  const [userSequence, setUserSequence] = useState([]);

  // ИЗМЕНЕНИЕ: Используем resultMenuData для любого исхода раунда
  const [resultMenuData, setResultMenuData] = useState(null);
  const [replayMode, setReplayMode] = useState(false); // Режим прослушивания правильной мелодии после раунда

  const audioCtxRef = useRef(null);
  const isPlayingSequence = useRef(false);
  const currentRoundIdRef = useRef(0);

  // --- AUDIO ENGINE ---
  const initAudio = useCallback(() => {
    if (!audioCtxRef.current) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = new AudioContext();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
  }, []);

  const playNoteSound = useCallback((noteIndex, duration = 0.5, type = 'triangle') => {
    if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
    }
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    const freq = 261.63 * Math.pow(2, noteIndex / 12);

    osc.type = type;
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    const now = ctx.currentTime;

    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.1, now + 0.1);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + duration);

    osc.connect(gainNode);
    gainNode.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + duration);
  }, []);

  const playErrorSound = useCallback(() => {
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(100, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(50, ctx.currentTime + 0.3);
      gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3);
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
  }, []);

  // --- LOGIC ---

  const playSequence = useCallback(async (sequence, speed, sendServerSignal = true) => {
    if (isGameOverRef.current) return;
    if (isPlayingSequence.current) return;
    isPlayingSequence.current = true;

    setPlaybackIndex(-1);
    await new Promise(r => setTimeout(r, 800));

    for (let i = 0; i < sequence.length; i++) {
      if (!audioCtxRef.current) break;
      const note = sequence[i];
      setPlaybackIndex(i);
      setPressedKey(note);
      playNoteSound(note, speed / 1000);
      await new Promise(r => setTimeout(r, speed * 0.8));
      setPressedKey(null);
      await new Promise(r => setTimeout(r, speed * 0.2));
    }

    setPlaybackIndex(-1);
    isPlayingSequence.current = false;

    if (isGameStarted && sendServerSignal) {
        store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'start_playing' });
    }
  }, [playNoteSound, isGameStarted, store]);

  const handleKeyPress = (note) => {
    // Блокируем ввод, если меню результатов открыто
    if (!state || state.status !== 'play' || !isGameStarted || resultMenuData) return;
    if (userSequence.length >= state.sequenceLength) return;

    setPressedKey(note);
    playNoteSound(note, 0.4);
    window.croco?.haptic?.('selection');
    setTimeout(() => setPressedKey(null), 150);

    const newSequence = [...userSequence, note];
    setUserSequence(newSequence);

    if (newSequence.length === state.sequenceLength) {
        store.ref.work.methods.hostAction('mafiaMiniGameAction', {
            type: 'check_sequence',
            sequence: newSequence,
            roundId: state.roundId
        });
    }
  };

  const handleStartGame = () => {
      initAudio();
      setIsGameStarted(true);
      store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'init' });
  };

  // Кнопка "ДАЛЕЕ"
  const handleNextRound = () => {
      if (!resultMenuData) return;
      const { nextState } = resultMenuData;

      setResultMenuData(null);
      setUserSequence([]);

      setState(nextState);

      if (nextState.roundId !== currentRoundIdRef.current) {
         currentRoundIdRef.current = nextState.roundId;
         playSequence(nextState.sequence, nextState.speed, true);
      }
  };

  // Кнопка "ПОВТОРИТЬ"
  const handleRepeatMelody = async () => {
      if (!resultMenuData) return;

      const { correctSequence, correctSpeed, nextState } = resultMenuData;
      setResultMenuData(null); // Скрываем меню

      setReplayMode(true); // Включаем режим индикации "Верная мелодия"

      // Проигрываем старую (верную) мелодию, без сигнала серверу
      await playSequence(correctSequence, correctSpeed || 600, false);

      setReplayMode(false);
      setUserSequence([]);

      // Переходим к следующему раунду
      setState(nextState);

      if (nextState.roundId !== currentRoundIdRef.current) {
          currentRoundIdRef.current = nextState.roundId;
          playSequence(nextState.sequence, nextState.speed, true);
      }
  };


  useEffect(() => {
    if (!socket) return;

    const handleEvent = async (data) => {
      if (!isGameStarted && data.type !== 'game_over') return;

      if (data.type === 'update') {
        setState(data.state);
        if (data.state.roundId !== currentRoundIdRef.current) {
             currentRoundIdRef.current = data.state.roundId;
             if (data.state.status === 'listen' && data.state.sequence) {
                 playSequence(data.state.sequence, data.state.speed);
             }
        }
      }
      else if (data.type === 'next') {
        const { result, feedback, state: newState } = data; // result: 'perfect', 'almost', 'good', 'ok', 'fail'

        // Проигрываем звук результата
        if (result === 'perfect' || result === 'almost') {
            window.croco?.haptic?.('success');
            // Можно добавить победный джингл здесь
        } else if (result === 'good' || result === 'ok') {
             window.croco?.haptic?.('impactMedium');
        } else {
            window.croco?.haptic?.('error');
            playErrorSound();
        }

        // Небольшая задержка перед показом меню
        await new Promise(r => setTimeout(r, 500));

        // ИЗМЕНЕНИЕ: Всегда показываем меню результатов
        setResultMenuData({
            status: result, // 'perfect', 'almost', etc.
            points: feedback.points,
            correctSequence: feedback.correctSequence,
            correctSpeed: feedback.correctSpeed,
            nextState: newState
        });
      }
      else if (data.type === 'game_over') {
        let final = data.finalData;
        if (!final && data.my) {
            final = { my: data.my, leaderboard: data.leaderboard };
        }
        setFinalData(final || {});
        isGameOverRef.current = true;
        if (final?.my?.isWin) window.croco?.haptic?.('success');
        else window.croco?.haptic?.('error');
      }
    };

    socket.on('arcade:event', handleEvent);
    return () => socket.off('arcade:event', handleEvent);
  }, [socket, store, isGameStarted, initAudio, playErrorSound, playSequence]);


  // --- RENDERERS ---

  const renderWhiteKey = (keyDef) => {
     const isPressed = pressedKey === keyDef.note;
     return (
        <div
          key={keyDef.note}
          className={`key-white ${isPressed ? 'pressed' : ''}`}
          data-note={keyDef.note}
          onPointerDown={(e) => { e.preventDefault(); handleKeyPress(keyDef.note); }}
        >
           <span className="key-label">{keyDef.label}</span>
        </div>
     );
  };

  const renderBlackKey = (keyDef) => {
      let leftPercent = 0;
      if (keyDef.note === 1) leftPercent = 14.28;
      if (keyDef.note === 3) leftPercent = 28.57;
      if (keyDef.note === 6) leftPercent = 57.14;
      if (keyDef.note === 8) leftPercent = 71.42;
      if (keyDef.note === 10) leftPercent = 85.71;

      const isPressed = pressedKey === keyDef.note;
      return (
         <div
            key={keyDef.note}
            className={`key-black ${isPressed ? 'pressed' : ''}`}
            data-note={keyDef.note}
            style={{ left: `${leftPercent}%` }}
            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); handleKeyPress(keyDef.note); }}
         />
      );
  };

  // 1. Финальный экран
  if (finalData) {
      return (
              <PianoFinalScreen
                  t={t}
                  finalData={finalData}
                  myId={store.ref.telegram.telegramUserId}
                  onClose={onClose}
              />
      );
  }

  // 2. Стартовый экран
  if (!isGameStarted) {
      return (
              <PianoStartScreen t={t} onStart={handleStartGame} />
      );
  }

  if (!state) return null;

  // Логика блокировки
  const isBlockingPhase = state.status === 'listen' || replayMode || playbackIndex !== -1;

  let statusText = isBlockingPhase ? t('arcade.piano_listen_phase') : t('arcade.piano_play_phase');
  let statusClass = isBlockingPhase ? 'listen' : 'play';

  if (replayMode) {
      statusText = t('arcade.piano_correct_melody', 'ВЕРНАЯ МЕЛОДИЯ');
      statusClass = 'replay';
  }

  return (
    <div className="piano-board">
       {/* HEADER */}
       <div className="piano-header">
           <div className="piano-score">{t('arcade.wordduel_score')}: {state.score}</div>
           <div className={`piano-status ${statusClass}`}>{statusText}</div>
           <div style={{color: '#888'}}>
              {(timeLeft / 60) | 0}:{(timeLeft % 60).toString().padStart(2, '0')}
           </div>
       </div>

       {/* PROGRESS DOTS */}
       <div className="piano-progress-bar">
           {Array.from({length: state.sequenceLength}).map((_, i) => {
              let dotClass = 'piano-dot';

              if (state.status === 'play' && !isBlockingPhase) {
                  // ФАЗА ИГРЫ
                  if (i < userSequence.length) {
                      const userNote = userSequence[i];
                      const correctNote = state.sequence[i];
                      if (userNote === correctNote) {
                          dotClass += ' correct';
                      } else {
                          dotClass += ' wrong';
                      }
                  } else if (i === userSequence.length) {
                       dotClass += ' current';
                  }
              } else {
                  // ФАЗА СЛУШАНИЯ или РЕПЛЕЯ
                  if (i <= playbackIndex) dotClass += ' filled-yellow';
              }

              return <div key={i} className={dotClass} />;
           })}
       </div>

       {/* MENU (OVERLAY) - Окно результата раунда */}
       <PianoRoundResultScreen
            t={t}
            resultData={resultMenuData}
            onRepeat={handleRepeatMelody}
            onNext={handleNextRound}
       />

       {/* KEYBOARD */}
       <div className="piano-keys-container">
           {KEYS.filter(k => k.type === 'white').map(renderWhiteKey)}
           {KEYS.filter(k => k.type === 'black').map(renderBlackKey)}

           {(isBlockingPhase || resultMenuData) && <div className="piano-blocker" />}
       </div>
    </div>
  );
};

export default Piano;